import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "../api/axios";
import { useNavigate, useLocation } from "react-router-dom";
import { frontendAccessRules } from "../utility/accessRules"; // ✅ Import access rules
import Loading from './../components/Loading';

const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes
const SESSION_MAX_DURATION = 4 * 60 * 60 * 1000; // 4 hours



const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [auth, setAuth] = useState({ isAuthenticated: false, user: null });
  const [loading, setLoading] = useState(true); // ✅ Start as true
  const isCheckingSession = useRef(false);

  const activityDetected = useRef(false);
  const inactivityTimer = useRef(null);
  const sessionStartTime = useRef(Date.now());
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  const logout = useCallback(async () => {
    try {
      const csrfToken = document.cookie
        .split("; ")
        .find(row => row.startsWith("csrfToken="))
        ?.split("=")[1];
  
      if (!csrfToken) {
        console.warn("🚨 Missing CSRF token. Proceeding with frontend logout.");
      } else {
        await axios.post("/api/user/logout", {}, {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken }
        });
      }
  
    } catch (error) {
      if (error.response?.status === 401) {
        console.warn("⚠️ Already logged out on the server.");
      } else {
        console.error("Logout error:", error);
      }
    } finally {
      setAuth({ isAuthenticated: false, user: null });
      navigate("/login");
    }
  }, [navigate, setAuth]); // ✅ Dependencies added  

  // ✅ Track user activity and reset inactivity timer
  useEffect(() => {
    const handleActivity = () => {
      activityDetected.current = true;

      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);

      inactivityTimer.current = setTimeout(async () => {
        if (auth.isAuthenticated) {
          console.log("🚨 Logging out due to inactivity.");
          await logout();
        }
      }, INACTIVITY_LIMIT);
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("scroll", handleActivity);

    handleActivity(); // Start initial timer on load

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [auth.isAuthenticated, logout]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (auth.isAuthenticated) {
        const elapsed = Date.now() - sessionStartTime.current;
        if (elapsed >= SESSION_MAX_DURATION) {
          console.log("🚨 Hard session limit reached. Logging out.");
          logout();
        }
      }
    }, 60 * 1000); // ✅ Check every minute
  
    return () => clearInterval(interval);
  }, [auth.isAuthenticated, logout]);
  

  //console.log(`🔄 AuthProvider Rendered at ${location.pathname}`);

  useEffect(() => {
    const checkSession = async () => {
      if (isCheckingSession.current) return; // ✅ Prevents infinite calls
      isCheckingSession.current = true;
  
      try {
        const response = await axios.get("/api/auth/me", { withCredentials: true });
        if (response.data.user) {
          setAuth({ isAuthenticated: true, user: response.data.user });
        } else {
          setAuth({ isAuthenticated: false, user: null });
        }
      } catch (error) {
        if (error.response?.status === 401) {
          if (auth.isAuthenticated) {
            await logout(); // ✅ logout is safe because it's wrapped in `useCallback`
          } else {
            setAuth({ isAuthenticated: false, user: null });
          }
        } else {
          console.error("⚠️ Auth check error:", error);
        }
      } finally {
        isCheckingSession.current = false;
        setLoading(false);
      }
    };
  
    checkSession();
  }, [auth.isAuthenticated, logout]); // ✅ Added missing dependencies  
  


    
  

  const hasAccess = (route) => {
    if (loading || !auth.isAuthenticated || !auth.user) {
      // Avoid logging during initial load
      if (!loading) {
        console.log(`❌ Access denied (not authenticated) for route: ${route}`);
      }
      return false;
    }
  
    const matchingRule = Object.keys(frontendAccessRules).find((baseRoute) =>
      route.startsWith(baseRoute)
    );
  
    if (!matchingRule) {
      console.log(`✅ No specific rule found for ${route}, allowing access.`);
      return true;
    }
  
    const allowedRoles = frontendAccessRules[matchingRule];
    const userHasAccess = allowedRoles.includes(auth.user.role);
  
    if (!userHasAccess) {
      console.log(`❌ Role '${auth.user.role}' not permitted for route: ${route}`);
    }
  
    return userHasAccess;
  };
  

  const login = async (credentials) => {
    try {
        const response = await axios.post("/api/auth/login", credentials, { withCredentials: true });
  
        if (response.status === 206) {
            return response.data; // ✅ 2FA required, return response without logging in
        } else if (response.data.user) {
            setAuth({ isAuthenticated: true, user: response.data.user });
            sessionStartTime.current = Date.now();
  
            // ✅ Redirect based on role
            if (["admin", "siteadmin"].includes(response.data.user.role)) {
                window.location.href = "/admin/dashboard";
            } else {
                window.location.href = "/";
            }
  
            return { success: true };
        }
    } catch (error) {
        return { error: error.response?.data?.error || "Login failed. Please try again." };
    }
  };  


  

  
  


  return (
<AuthContext.Provider value={{ auth, login, logout, hasAccess, loading }}>
  {loading && isAdminRoute && <Loading />}

  {loading && !isAdminRoute && (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'white', // or your site's background
        zIndex: 9999,
      }}
    />
  )}

  {/* Only render app after loading is done */}
  {!loading && children}
</AuthContext.Provider>

  );
};

export default AuthProvider;